import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Trash, X } from '@phosphor-icons/react';

/**
 * Confirmation dialog used by every bulk-delete flow. Enforces the same two
 * safety guards for a destructive action:
 *   1. User must type "DELETE" (exact, uppercase) in a text input
 *   2. User must give a reason of at least 5 characters (audit-log fodder)
 *
 * Matches the President user-delete flow so the pattern feels consistent
 * everywhere destructive things happen.
 */
export default function BulkDeleteDialog({
  open, onClose, onConfirm,
  entityLabel = 'record',      // e.g. "lead", "customer"
  entityLabelPlural = 'records', // e.g. "leads", "customers"
  count = 0,
  extraWarning = null,          // optional JSX shown above the inputs
  loading = false,
}) {
  const [typedDelete, setTypedDelete] = useState('');
  const [reason, setReason] = useState('');
  const canConfirm = typedDelete === 'DELETE' && reason.trim().length >= 5 && !loading;

  const handleClose = () => {
    if (loading) return;
    setTypedDelete('');
    setReason('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    await onConfirm({ reason: reason.trim() });
    setTypedDelete('');
    setReason('');
  };

  const label = count === 1 ? entityLabel : entityLabelPlural;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-[500px]" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash size={20} weight="fill" />
            Permanently delete {count} {label}?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
            This cannot be undone. All {label} listed will be removed from the database.
            {extraWarning}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3B5A54] mb-1">
              Type <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-red-700">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={typedDelete}
              onChange={(e) => setTypedDelete(e.target.value)}
              placeholder="DELETE"
              autoFocus
              disabled={loading}
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-500"
              data-testid="bulk-delete-confirm-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3B5A54] mb-1">Reason (min 5 chars, required for audit log)</label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Duplicate entries after CSV re-upload"
              disabled={loading}
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-500"
              data-testid="bulk-delete-reason-input"
            />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm text-[#3B5A54] border border-[#D5E8E4] rounded-lg hover:bg-[#F4F9F7]"
            data-testid="bulk-delete-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={`px-4 py-2 text-sm font-semibold rounded-lg text-white ${canConfirm ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'}`}
            data-testid="bulk-delete-confirm"
          >
            {loading ? 'Deleting...' : `Delete ${count} ${label}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Sticky bottom bar shown whenever any rows are selected. Keeps the primary
 * bulk actions one thumb-reach away without occluding the list.
 */
export function BulkActionBar({ count, onClear, children, testid = 'bulk-action-bar' }) {
  if (count === 0) return null;
  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-5 py-3 bg-[#052A23] text-white rounded-full shadow-xl border border-[#00a69c]/40 animate-slide-in"
      data-testid={testid}
    >
      <span className="text-sm font-semibold" data-testid={`${testid}-count`}>
        {count} selected
      </span>
      <div className="w-px h-6 bg-white/20" />
      {children}
      <button
        type="button"
        onClick={onClear}
        className="ml-1 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
        title="Clear selection"
        data-testid={`${testid}-clear`}
      >
        <X size={16} />
      </button>
    </div>
  );
}
